import { formatNumber, formatTimeLabel, timeTicks } from "@/lib/chart/format"
import {
  clearSurface,
  drawCrosshair,
  drawEmptyState,
  drawGrid,
  drawLine,
  drawTimeAxis,
  drawValueAxis,
  plotRect,
  TIME_TICK_SPACING,
  type PlotRect,
} from "@/lib/chart/primitives"
import { extent, linearScale } from "@/lib/chart/scales"
import { parseUtcDate } from "@/lib/chart/timeframes"
import { CHART_COLORS, type ChartTheme } from "@/lib/chart/theme"
import type { SeriesBand, SeriesChartData, SeriesDefinition, SeriesReferenceLine } from "@/types/seriesChart"

export interface SeriesRenderOptions {
  ctx: CanvasRenderingContext2D
  width: number
  height: number
  data: SeriesChartData
  visibleKeys: string[]
  start: number
  end: number
  hoverIndex: number | null
  /** Pointer y in CSS pixels, for the horizontal crosshair and its value label. */
  hoverY?: number | null
  /** Precomputed `seriesTimeline(data, visibleKeys)`, so redraws skip rebuilding it. */
  timeline?: string[]
  theme?: ChartTheme
}

const PANE_GAP = 14

interface PaneSpec {
  rect: PlotRect
  series: SeriesDefinition[]
  bands: SeriesBand[]
  unit: string | null | undefined
  fixed: { min?: number | null; max?: number | null }
  references: SeriesReferenceLine[]
}

interface PaneScale {
  rect: PlotRect
  scale: ReturnType<typeof linearScale>
  step: number | undefined
}

export function renderSeriesChart(options: SeriesRenderOptions): void {
  const { ctx, width, height, data, visibleKeys, start, end, hoverIndex, hoverY = null } = options
  const theme = options.theme ?? CHART_COLORS
  clearSurface(ctx, theme, width, height)

  const timeline = options.timeline ?? buildTimeline(data, visibleKeys)
  if (timeline.length === 0) {
    drawEmptyState(ctx, theme, width, height, "No series data available yet")
    return
  }

  const clampedStart = Math.max(0, Math.min(start, timeline.length - 1))
  const clampedEnd = Math.max(clampedStart + 1, Math.min(end, timeline.length))
  const visibleTimes = timeline.slice(clampedStart, clampedEnd)
  const series = data.series.filter((item) => visibleKeys.includes(item.key))
  const mainSeries = series.filter((item) => item.pane !== "lower")
  const lowerSeries = series.filter((item) => item.pane === "lower")
  const timeFormat = data.time_format ?? "day"

  const plotBottom = height - 28
  const lowerHeight = lowerSeries.length > 0 ? Math.round((plotBottom - 16) * 0.34) : 0
  const mainBottom = lowerSeries.length > 0 ? plotBottom - lowerHeight - PANE_GAP : plotBottom
  const specs: PaneSpec[] = [
    {
      rect: plotRect(width, 12, 70, 16, mainBottom),
      series: mainSeries,
      bands: data.bands ?? [],
      unit: data.unit,
      fixed: { min: data.y_min, max: data.y_max },
      references: data.reference_lines ?? [],
    },
  ]
  if (lowerSeries.length > 0) {
    specs.push({
      rect: plotRect(width, 12, 70, plotBottom - lowerHeight, plotBottom),
      series: lowerSeries,
      bands: [],
      unit: data.lower_unit,
      fixed: {},
      references: [],
    })
  }

  const main = specs[0].rect
  const ratios = seriesXRatios(visibleTimes, data.x_scale)
  const x = (index: number): number =>
    main.left + (ratios ? ratios[index] : (index + 0.5) / visibleTimes.length) * main.width
  const ticks = timeTicks(visibleTimes, Math.floor(main.width / TIME_TICK_SPACING))
  const tickXs = ticks.map((tick) => x(tick.index))

  const panes: PaneScale[] = []
  for (const spec of specs) {
    const pane = drawPane(ctx, theme, spec, visibleTimes, x, tickXs, panes.length === 0)
    if (pane) panes.push(pane)
  }
  if (panes.length === 0) {
    clearSurface(ctx, theme, width, height)
    drawEmptyState(ctx, theme, width, height, "No series data available yet")
    return
  }

  drawTimeAxis(ctx, theme, { ...main, bottom: plotBottom }, ticks, x)

  if (hoverIndex !== null && hoverIndex >= clampedStart && hoverIndex < clampedEnd) {
    const localIndex = hoverIndex - clampedStart
    const hovered = hoverY === null ? null : panes.find((pane) => hoverY >= pane.rect.top && hoverY <= pane.rect.bottom)
    drawCrosshair(ctx, theme, {
      x: x(localIndex),
      y: hovered ? hoverY : null,
      plot: hovered?.rect ?? main,
      bottom: plotBottom,
      width,
      timeLabel: formatTimeLabel(visibleTimes[localIndex], timeFormat),
      valueLabel: hovered && hoverY !== null ? formatNumber(hovered.scale.invert(hoverY), { step: hovered.step }) : null,
    })
  }
}

function drawPane(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  spec: PaneSpec,
  times: string[],
  x: (index: number) => number,
  tickXs: number[],
  primary: boolean,
): PaneScale | null {
  const { rect, series, bands, fixed, references } = spec
  const columns = series.filter((item) => (item.style ?? "line") === "column")
  const values = collectValues(series, bands, times)
  for (const line of references) values.push(line.value)
  const range = extent(values)
  if (!range && primary) return null
  const [min, max] = range ?? [0, 1]

  const padding = Math.max((max - min) * 0.08, Math.abs(max) * 0.002, 0.01)
  let domainMin = columns.length > 0 ? Math.min(0, min - padding) : min - padding
  let domainMax = columns.length > 0 ? Math.max(0, max + padding) : max + padding
  // A fixed range (e.g. 0–100) wins unless the data actually falls outside it, with a little
  // headroom so a line sitting on the bound isn't clipped.
  const fixedPad = fixed.min != null && fixed.max != null ? (fixed.max - fixed.min) * 0.03 : 0
  if (fixed.min != null) domainMin = Math.min(fixed.min - fixedPad, min)
  if (fixed.max != null) domainMax = Math.max(fixed.max + fixedPad, max)
  const scale = linearScale([domainMin, domainMax], [rect.bottom, rect.top])

  const valueTicks = scale.ticks(Math.max(3, Math.floor(rect.height / (primary ? 60 : 40))))
  const step = valueTicks.length > 1 ? valueTicks[1] - valueTicks[0] : undefined
  drawGrid(ctx, theme, rect, valueTicks, scale.map, tickXs)

  ctx.save()
  ctx.beginPath()
  ctx.rect(rect.left, rect.top, rect.width, rect.height)
  ctx.clip()
  for (const line of references) drawReferenceLine(ctx, theme, rect, scale.map(line.value), line.label)
  for (const band of bands) drawBand(ctx, theme, band, times, x, scale.map)
  columns.forEach((item, position) =>
    drawColumns(ctx, theme, item, times, x, scale.map, rect, { position, count: columns.length }),
  )
  for (const item of series) {
    if ((item.style ?? "line") !== "column") drawLineSeries(ctx, theme, item, times, x, scale.map)
  }
  ctx.restore()

  drawValueAxis(ctx, theme, rect, valueTicks, scale.map, (value) => formatNumber(value, { step }), primary ? spec.unit ?? undefined : undefined)
  if (!primary && spec.unit) {
    ctx.font = "10px ui-sans-serif, system-ui"
    ctx.textBaseline = "top"
    ctx.fillText(spec.unit, rect.right + 8, rect.top - 12, 62)
  }
  return { rect, scale, step }
}

function drawReferenceLine(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  rect: PlotRect,
  y: number,
  label: string,
): void {
  ctx.strokeStyle = theme.mutedLine
  ctx.globalAlpha = 0.7
  ctx.lineWidth = 1
  ctx.setLineDash([5, 4])
  ctx.beginPath()
  ctx.moveTo(rect.left, y)
  ctx.lineTo(rect.right, y)
  ctx.stroke()
  ctx.setLineDash([])
  ctx.globalAlpha = 1
  ctx.fillStyle = theme.text
  ctx.font = "10px ui-sans-serif, system-ui"
  ctx.textAlign = "right"
  ctx.textBaseline = "bottom"
  ctx.fillText(label, rect.right - 4, y - 3)
  ctx.textAlign = "start"
}

function buildTimeline(data: SeriesChartData, visibleKeys: string[]): string[] {
  const times = new Set<string>()
  for (const item of data.series) {
    if (!visibleKeys.includes(item.key)) continue
    for (const [time] of item.points) times.add(time)
  }
  for (const band of data.bands ?? []) {
    for (const [time] of band.low) times.add(time)
    for (const [time] of band.high) times.add(time)
  }
  return Array.from(times).sort()
}

function collectValues(series: SeriesDefinition[], bands: SeriesBand[], times: string[]): number[] {
  const values: number[] = []
  const timeSet = new Set(times)
  for (const item of series) {
    for (const [time, value] of item.points) {
      if (timeSet.has(time) && value != null && Number.isFinite(value)) values.push(value)
    }
  }
  for (const band of bands) {
    for (const [time, value] of [...band.low, ...band.high]) {
      if (timeSet.has(time) && value != null && Number.isFinite(value)) values.push(value)
    }
  }
  return values
}

function pointMap(points: Array<[string, number | null]>): Map<string, number | null> {
  return new Map(points)
}

function drawBand(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  band: SeriesBand,
  times: string[],
  x: (index: number) => number,
  y: (value: number) => number,
): void {
  const lows = pointMap(band.low)
  const highs = pointMap(band.high)
  ctx.beginPath()
  let started = false
  times.forEach((time, index) => {
    const high = highs.get(time)
    if (high == null || !Number.isFinite(high)) {
      started = false
      return
    }
    const px = x(index)
    const py = y(high)
    if (!started) {
      ctx.moveTo(px, py)
      started = true
    } else {
      ctx.lineTo(px, py)
    }
  })
  for (let index = times.length - 1; index >= 0; index -= 1) {
    const low = lows.get(times[index])
    if (low == null || !Number.isFinite(low)) continue
    ctx.lineTo(x(index), y(low))
  }
  ctx.closePath()
  ctx.fillStyle = band.color ?? theme.bandFill
  ctx.fill()
}

function drawLineSeries(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  item: SeriesDefinition,
  times: string[],
  x: (index: number) => number,
  y: (value: number) => number,
): void {
  const values = pointMap(item.points)
  // Only times this series reports: a weekly series on a daily timeline stays one connected
  // line. An explicit null still breaks it.
  const points: Array<{ x: number; y: number | null }> = []
  times.forEach((time, index) => {
    if (!values.has(time)) return
    const value = values.get(time)
    points.push({ x: x(index), y: value == null || !Number.isFinite(value) ? null : y(value) })
  })
  drawLine(ctx, points, item.color, 2)
  if (!item.markers) return
  // Dots get a ring in the surface colour so they stay distinct where the line is dense.
  const radius = Math.max(2, Math.min(4, (x(1) - x(0)) * 0.35))
  ctx.fillStyle = item.color
  ctx.strokeStyle = theme.background
  ctx.lineWidth = 1.5
  for (const point of points) {
    if (point.y === null) continue
    ctx.beginPath()
    ctx.arc(point.x, point.y, radius, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
  }
}

/**
 * A lone column series is coloured by sign (gains vs draws); several column series sit
 * side by side in their own colours instead of painting over each other.
 */
function drawColumns(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  item: SeriesDefinition,
  times: string[],
  x: (index: number) => number,
  y: (value: number) => number,
  plot: PlotRect,
  group: { position: number; count: number },
): void {
  const values = pointMap(item.points)
  const zero = y(0)
  const slot = (plot.width / times.length) * 0.7
  const barWidth = Math.max(1, Math.min(14, slot / group.count - (group.count > 1 ? 1 : 0)))
  const offset = (group.position - (group.count - 1) / 2) * (slot / group.count)
  times.forEach((time, index) => {
    const value = values.get(time)
    if (value == null || !Number.isFinite(value)) return
    const top = y(value)
    const height = Math.max(1.5, Math.abs(top - zero))
    ctx.fillStyle = group.count > 1 ? item.color : value >= 0 ? theme.up : theme.down
    ctx.fillRect(x(index) + offset - barWidth / 2, Math.min(top, zero), barWidth, height)
  })
}

/**
 * Horizontal position (0–1 of the plot width) of each visible point when the chart uses a
 * time scale, or null for the default even spacing. Ends are inset so edge points stay clear.
 */
export function seriesXRatios(times: string[], xScale: SeriesChartData["x_scale"]): number[] | null {
  if (xScale !== "time" || times.length < 2) return null
  const stamps = times.map((time) => parseUtcDate(time)?.getTime() ?? Number.NaN)
  if (stamps.some((stamp) => Number.isNaN(stamp))) return null
  const first = stamps[0]
  const span = stamps[stamps.length - 1] - first
  if (span <= 0) return null
  const inset = Math.min(0.5 / times.length, 0.04)
  return stamps.map((stamp) => inset + ((stamp - first) / span) * (1 - inset * 2))
}

export function seriesTimeline(data: SeriesChartData, visibleKeys: string[]): string[] {
  return buildTimeline(data, visibleKeys)
}
